import type { ReactNode } from "react";
import { cx } from "./cx.js";
import { Icon } from "./Icon.jsx";

export type BannerTone = "info" | "warn" | "alarm" | "ok";

const TONE: Record<BannerTone, { cls: string; icon: string }> = {
  info: { cls: "border-line bg-info-subtle", icon: "info" },
  warn: { cls: "border-line bg-warn-subtle", icon: "warning" },
  alarm: { cls: "border-line bg-alarm-subtle", icon: "error" },
  ok: { cls: "border-line bg-ok-subtle", icon: "check_circle" },
};
const ICON_TEXT: Record<BannerTone, string> = {
  info: "text-info",
  warn: "text-warn",
  alarm: "text-alarm",
  ok: "text-ok",
};

/** A slim, in-flow message. An alarm banner is announced at once (role=alert); the rest politely (role=status). */
export function Banner({
  tone = "info",
  title,
  icon,
  actions,
  children,
  ...rest
}: {
  tone?: BannerTone;
  title?: string;
  icon?: string;
  actions?: ReactNode;
  children?: ReactNode;
  "data-testid"?: string;
  "aria-label"?: string;
}) {
  const t = TONE[tone];
  return (
    <section
      role={tone === "alarm" ? "alert" : "status"}
      className={cx(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 text-sm text-ink",
        t.cls,
      )}
      {...rest}
    >
      <Icon name={icon ?? t.icon} size={20} className={ICON_TEXT[tone]} />
      <div className="min-w-0 flex-1">
        {title ? <span className="font-semibold">{title}</span> : null}
        {title && children ? " " : null}
        {children}
      </div>
      {actions}
    </section>
  );
}
